import React, { useState, useEffect, useMemo } from "react";
import {
    Container,
    Card,
    CardBody,
    CardTitle,
    Table,
    Spinner,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    Input,
    ListGroup,
    ListGroupItem,
    FormGroup,
    Label,
} from "reactstrap";
import Session from "../auth/Session";
import SubCustomerForm from "./SubCustomerForm";
import useSortableData from "./useSortableData";
import SortableHeader from "./SortableHeader";

const SubCustomerGrid = () => {
    const apiBaseUrl = "/api/subcustomers";
    const customersApi = "/api/customers";

    const [subCustomers, setSubCustomers] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [filterCustomer, setFilterCustomer] = useState("");
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [showCustomerList, setShowCustomerList] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [loading, setLoading] = useState(false);

    const [editing, setEditing] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const [nameSearch, setNameSearch] = useState("");
    const [includeInactive, setIncludeInactive] = useState(false);

    const filteredSubCustomers = useMemo(
        () => subCustomers.filter((sc) =>
            (sc.name || "").toLowerCase().includes(nameSearch.toLowerCase())
        ),
        [subCustomers, nameSearch]
    );

    const sortGetters = useMemo(() => ({
        manager: (sc) => sc.manager?.userName || sc.managerId,
        customer: (sc) => sc.customer?.name || sc.customerId,
    }), []);

    const { items: sortedSubCustomers, sortConfig, requestSort } = useSortableData(
        filteredSubCustomers,
        { key: "name", direction: "asc", getters: sortGetters }
    );

    const fetchSubCustomers = async (customerId = null, withInactive = includeInactive) => {
        setLoading(true);
        try {
            const url = customerId
                ? `${apiBaseUrl}/ByCustomer/${customerId}?includeInactive=${withInactive}`
                : `${apiBaseUrl}?includeInactive=${withInactive}`;
            const token = await Session.getToken();
            const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            setSubCustomers(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchCustomers = async () => {
            const token = await Session.getToken();
            const res = await fetch(customersApi, { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            setCustomers(data);
        };

        fetchCustomers().catch(console.error);

        fetchSubCustomers();
    }, []);

    useEffect(() => {
        if (filterCustomer.trim() === "") {
            setFilteredCustomers(customers);
        } else {
            const filtered = customers.filter((c) =>
                c.name.toLowerCase().includes(filterCustomer.toLowerCase())
            );
            setFilteredCustomers(filtered);
        }
    }, [filterCustomer, customers]);

    const handleCustomerSelect = (c) => {
        setSelectedCustomer(c);
        setFilterCustomer(c.name);
        setShowCustomerList(false);
        fetchSubCustomers(c.id);
    };

    const clearCustomerFilter = () => {
        setSelectedCustomer(null);
        setFilterCustomer("");
        setFilteredCustomers(customers);
        setShowCustomerList(false);
        fetchSubCustomers();
    };

    const openCustomerDropdown = () => {
        setFilteredCustomers(filterCustomer.trim() === "" ? customers : filteredCustomers);
        setShowCustomerList(true);
    };

    return (
        <Container className="mt-5">
            <Card>
                <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <CardTitle tag="h5">Sub-Customer Management</CardTitle>
                        <Button color="primary" onClick={() => { setEditing(null); setModalOpen(true); }}>
                            + New Sub-Customer
                        </Button>
                    </div>

                    {/* Customer Filter */}
                    <div className="mb-3 position-relative">
                        <div className="input-group">
                            <Input
                                placeholder="Filter by Customer..."
                                value={filterCustomer}
                                onFocus={openCustomerDropdown}
                                onBlur={() => setTimeout(() => setShowCustomerList(false), 150)}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setFilterCustomer(value);
                                    setShowCustomerList(true);
                                    if (value.trim() === "") {
                                        setSelectedCustomer(null);
                                        fetchSubCustomers();
                                    }
                                }}
                            />
                            <Button
                                type="button"
                                color="secondary"
                                outline
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                    setFilteredCustomers(filterCustomer.trim() === "" ? customers : filteredCustomers);
                                    setShowCustomerList(!showCustomerList);
                                }}
                            >
                                v
                            </Button>
                        </div>
                        {showCustomerList && (
                            <ListGroup className="position-absolute w-100" style={{ zIndex: 1000, maxHeight: 260, overflowY: "auto" }}>
                                {selectedCustomer && (
                                    <ListGroupItem action onMouseDown={(e) => { e.preventDefault(); clearCustomerFilter(); }}>
                                        Clear filter
                                    </ListGroupItem>
                                )}
                                {filteredCustomers.length === 0 ? (
                                    <ListGroupItem disabled>No matches</ListGroupItem>
                                ) : (
                                    filteredCustomers.map((c) => (
                                        <ListGroupItem key={c.id} action onMouseDown={(e) => { e.preventDefault(); handleCustomerSelect(c); }}>
                                            {c.name}
                                        </ListGroupItem>
                                    ))
                                )}
                            </ListGroup>
                        )}
                    </div>

                    {/* Sub-Customer Name Search */}
                    <div className="mb-3 d-flex align-items-center gap-3">
                        <Input
                            placeholder="Search by Sub-Customer name..."
                            value={nameSearch}
                            onChange={(e) => setNameSearch(e.target.value)}
                        />
                        <FormGroup check className="mb-0 text-nowrap">
                            <Label check>
                                <Input
                                    type="checkbox"
                                    checked={includeInactive}
                                    onChange={(e) => {
                                        const next = e.target.checked;
                                        setIncludeInactive(next);
                                        fetchSubCustomers(selectedCustomer?.id, next);
                                    }}
                                />{" "}
                                Show inactive
                            </Label>
                        </FormGroup>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                        </div>
                    ) : (
                        <Table bordered hover responsive>
                            <thead className="table-light">
                                <tr>
                                    <SortableHeader label="Name" sortKey="name" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Warehouse #" sortKey="warehouseNumber" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Contact" sortKey="contactName" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Email" sortKey="contactEmail" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="City" sortKey="city" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="State" sortKey="state" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Manager" sortKey="manager" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Customer" sortKey="customer" sortConfig={sortConfig} onSort={requestSort} />
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedSubCustomers.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" className="text-center">No sub-customers found</td>
                                    </tr>
                                ) : (
                                    sortedSubCustomers.map((sc) => (
                                        <tr key={sc.id} className={sc.isActive === false ? "text-muted fst-italic" : ""}>
                                            <td>{sc.name}</td>
                                            <td>{sc.warehouseNumber}</td>
                                            <td>{sc.contactName}</td>
                                            <td>{sc.contactEmail}</td>
                                            <td>{sc.city}</td>
                                            <td>{sc.state}</td>
                                            <td>{sc.manager?.userName || sc.managerId}</td>
                                            <td>{sc.customer?.name || sc.customerId}</td>
                                            <td className="text-end">
                                                <Button size="sm" color="info" onClick={() => { setEditing(sc); setModalOpen(true); }}>
                                                    Edit
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>

            <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
                <ModalHeader toggle={() => setModalOpen(false)}>
                    {editing ? "Edit Sub-Customer" : "New Sub-Customer"}
                </ModalHeader>
                <ModalBody>
                    <SubCustomerForm
                        subCustomer={editing}
                        apiBaseUrl={apiBaseUrl}
                        onSuccess={() => { setModalOpen(false); fetchSubCustomers(selectedCustomer?.id); }}
                        onCancel={() => setModalOpen(false)}
                    />
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default SubCustomerGrid;
